import React, { useContext, useEffect, useMemo } from 'react';
import Layout from '@/components/layout';
import PaymentOption from './_payment-options';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import { REBRAND_COLOR, PADDING } from '@latitude/core/utils/constants';
import PageData from './data/low-rate-data.json';
import { Accordion } from '@latitude/accordion-rebrand';
import { Link } from '@latitude/link';
import { AnalyticsLocationProvider } from 'latitude-analytics';
import { PageContext } from '@/context/PageContext';
import PromoBanner from '@/components/PromoBannerRebrand/PromoBannerRebrand';
import lowRatePageDataBackUp from './data/low-rate-data-backup.json';
import FeatureCardList from '@/components/FeatureCardListRebrand/FeatureCardListRebrand';
import SvgInline from '@latitude/svg-inline';
import { CARD_CODE, CARD_DATA } from '@/data/pages/credit-cards/card-offer';
import { AnalyticsContext } from 'latitude-analytics';
import LabRatesAndFeesColumn from '@/components/LabRatesAndFeesColumnRebrand';
import Button from '@latitude/button-rebrand';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import FeatureGridV1 from '@/components/FeatureGridV1/FeatureGridV1';
import { RewardsCarasoule } from '@/components/RewardsCarasoule/RewardsCarasoule';
import StickyNavigation from '@/components/StickyNavigationRebrand/StickyNavigation';
import HowToApply from '@/components/HowToApplyRebrand/HowToApplyRebrand';
import Section from '@latitude/section-rebrand';
import Typography from '@latitude/typography';

const PAGE_NAVIGATION = [
  {
    anchor: 'benefits',
    label: 'Benefits'
  },
  {
    anchor: 'rewards',
    label: 'Rewards'
  },
  {
    anchor: 'rates',
    label: 'Rates & Fees'
  },
  {
    anchor: 'payment-options',
    label: 'Payment options'
  },
  {
    anchor: 'how-to-apply',
    label: 'How to apply'
  }
];

const FEATURE_GRID_DATA = {
  features: [
    {
      icon: <SvgInline name="icon-percent-rebrand" />,
      headingText: 'Let your budget breathe',
      paragraphText:
        'Enjoy a 13.99% p.a. purchase rate, so you can stretch your budget without stressing it.'
    },
    {
      icon: <SvgInline name="icon-gift-rebrand" />,
      headingText: 'Shop with confidence',
      paragraphText: (
        <>
          Delivery gone missing for your online purchases? Latitude’s E-Commerce
          Purchase Protection Insurance<sup>2</sup> has got you covered for
          eligible purchases, subject to T&Cs.
        </>
      )
    },
    {
      icon: <SvgInline name="icon-users-rebrand" />,
      headingText: 'Team up for even more rewards',
      paragraphText: `Add an additional cardholder at no extra cost and become the ultimate rewards-earning team.`,
      learnMoreUrl:
        'https://latitudefs.zendesk.com/hc/en-au/articles/36591875170577-Additional-Cardholders'
    },
    {
      icon: <SvgInline name="icon-seal-check-rebrand" />,
      headingText: 'Travel with peace of mind',
      paragraphText:
        'Wherever you go, our specialist team keeps your personal info safe from fraud and identity theft.',
      learnMoreUrl: 'https://www.latitudefinancial.com.au/scams-hub/'
    }
  ]
};

const HOW_TO_APPLY_DATA = {
  id: 'how-to-apply',
  howToApply: {
    sectionTitle: 'How To apply',
    items: [
      {
        title: 'Apply online',
        description: 'You can check your eligibility and apply in 10 minutes.'
      },
      {
        title: 'Wait a minute',
        description:
          '60 seconds is all it takes for us to respond to your application.'
      },
      {
        title: 'Ready, set, shop',
        description:
          'Simply activate your card when it arrives to start shopping.'
      }
    ]
  },
  eligibility: {
    sectionTitle: 'Eligibility',
    items: [
      {
        title: 'You must:',
        list: [
          'Be 18 years or over',
          'A resident of Australia or a temporary resident who legally has the right to work in Australia (and intend to stay) for at least the next 12 months.'
        ]
      },
      {
        title: 'Able to supply:',
        list: [
          "A copy of your driver's licence, passport or proof of age",
          'Details of your income, assets and debts',
          "Employer's name and contact details"
        ]
      }
    ]
  }
};

const cardData = CARD_DATA.filter(card => card.code !== CARD_CODE.LR);
const LowRateFinderExclusivePage = props => {
  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};
 const applyNowUrl = useMemo(() => {
    const baseUrl =
      state?.heroBannerData?.[0]?.slides?.[0]?.buttons?.[0]?.url;

    if (typeof window === "undefined") return baseUrl;

    const params = new URLSearchParams(window.location.search);
    const fdclid = params.get("fdclid");

    return fdclid
      ? `${baseUrl}&fdclid=${encodeURIComponent(fdclid)}`
      : baseUrl;
  }, [state]);

  // Create updated hero banner data
  const heroBannerData = useMemo(() => {
    const hero =
      state?.heroBannerData?.[0] ||
      lowRatePageDataBackUp?.heroBannerDataFinder?.[0];

    if (!hero) return hero;

    return {
      ...hero,
      slides: hero.slides?.map((slide, slideIndex) => {
        if (slideIndex !== 0) return slide;

        return {
          ...slide,
          buttons: slide.buttons?.map((button, buttonIndex) => {
            if (buttonIndex !== 0) return button;

            return {
              ...button,
              url: applyNowUrl
            };
          })
        };
      })
    };
  }, [state, applyNowUrl]);

 
  /** - END - */

  const [analytics] = useContext(AnalyticsContext);
  useEffect(() => {
    const sourceCode = localStorage.getItem('source_code') || 'IDAAA';
    // adding pageData to analytics
    analytics.pageData = {
      page_pageTitle: 'finder-exclusive',
      page_pageType: 'product-details-page',
      page_siteSection: 'cards',
      page_siteSubsection: 'low-rate',
      product_productid: ['CCAULRM'],
      application_sourcecode: sourceCode || 'IDAAA'
    };
  }, []);

  return (
    <div
      css={`
        .top-menu-items {
          visibility: hidden;
        }
        .logon-button-wrapper {
          visibility: hidden;
        }
        .search-button {
          visibility: hidden !important;
        }
        .hamburger-button {
          display: none !important;
        }
      `}
    >
      <Layout
        location={props.location}
        noIndex
        title="Finder eGift Card | Latitude Low Rate Mastercard"
      >
        <main>
          <HeroV2 {...heroBannerData} />

          <StickyNavigation
            items={PAGE_NAVIGATION}
            enableAnchorUpdate={true}
            trackId="sticky-get-started"
            stickyOffset={80}
          />

          <FeatureGridV1
            id="benefits"
            trackingId="lowrate"
            {...FEATURE_GRID_DATA}
          />

          <RewardsCarasoule
            id="rewards"
            title="Experience more with Latitude Rewards"
            subtitle="Each Latitude Reward is worth $1 towards dining and cinema experiences, eGift cards, and more - no points conversion needed."
            rewards={[
              {
                imageUrl: '/images/credit-cards/low-rate/rewards-1.webp',
                title: 'Earn on everyday spend',
                description: (
                  <span>
                    Get 3% back in Latitude Rewards<sup>1</sup> when you set up
                    recurring payments direct with participating utilities,
                    telco, and streaming services.{' '}
                    <Link
                      noStyle
                      style={{ textDecoration: 'underline' }}
                      href="https://assets.latitudefinancial.com/legals/terms-conditions/lowrate-rewards.pdf"
                      trackEventData={{
                        location:
                          'experience-more-with-latitude-rewards' ||
                          'rewards-carousel'
                      }}
                    >
                      T&Cs apply
                    </Link>
                    .
                  </span>
                )
              },
              {
                imageUrl: '/images/credit-cards/low-rate/rewards-2.webp',
                title: 'Browse tons of rewards',
                description:
                  'Use your Latitude Rewards for dining and cinema experiences, eGift cards, and more. Enjoy your rewards your way.'
              },
              {
                imageUrl: '/images/credit-cards/low-rate/rewards-3.webp',
                title: 'Redeem in seconds',
                description: (
                  <span>
                    Redeem your rewards in seconds. Just use the Latitude app or{' '}
                    <Link
                      noStyle
                      style={{ textDecoration: 'underline' }}
                      href="https://servicecentre.latitudefinancial.com.au/login?migration=true"
                    >
                      log in
                    </Link>{' '}
                    to the Latitude Service Centre.
                  </span>
                )
              }
            ]}
            rewardsCta={
              <Button
                className={`rewards-carousel-cta`}
                variant="secondary-blue"
                size="lg"
                ctaText="   Learn More"
                ctaUrl="/credit-cards/low-rate/latitude-rewards"
                trackEventData={{
                  category: 'text-link',
                  location: 'experience-more-with-latitude-rewards',
                  label: 'learn-more'
                }}
                trackId="rewards-carousel"
              />
            }
          />

          {state?.ratesAndFeesData?.[0] && (<LabRatesAndFeesColumn isRate={true} {...state.ratesAndFeesData[0]} />)}

          <PaymentOption />

          {state?.promoBannerData?.[0] && <PromoBanner {...state?.promoBannerData?.[0]}/>}

          <HowToApply data={HOW_TO_APPLY_DATA} />

          <FeatureCardList cardList={cardData} />

          <Section
            id="faq"
            css={`
              background-color: ${REBRAND_COLOR.WHITE};
              padding-top: ${PADDING.P32};
              padding-bottom: ${PADDING.P32};
            `}
          >
            <div
              data-contentful={state?.faqData?.[0]?.contentfulID}
              css={`
                display: flex;
                flex-direction: column;
                gap: 32px;
              `}
            >
              <Typography.H4>Frequently Asked Questions</Typography.H4>
              <AnalyticsLocationProvider
                location={'frequently asked questions'}
              >
                <Accordion
                  css={`
                    .bg-black-10 {
                      padding: 16px 24px;
                    }
                  `}
                  items={PageData.Faq}
                />
              </AnalyticsLocationProvider>
              <Button
                className={`faq-cta`}
                variant="secondary-blue"
                size="lg"
                ctaText={state?.faqData?.[0]?.cta?.text || 'See all FAQ'}
                ctaUrl={
                  state?.faqData?.[0]?.cta?.url ||
                  '/credit-cards/low-rate/faqs/'
                }
                trackEventData={{
                  label: state?.faqData?.[0]?.cta?.text || 'See all FAQ',
                  location: 'frequently asked questions'
                }}
                data-trackid="see-all-faq"
                css={`
                  width: fit-content;
                  margin: 0 auto;
                `}
              />
            </div>
          </Section>
         {state?.importantInfoData?.[0] && <ImportantInformation
            id="important-Info"
            css={`
              overflow-x: hidden;
              .row {
                @media (min-width: 1600px) {
                  padding-left: ${PADDING.P32};
                  padding-right: ${PADDING.P32};
                }
              }
            `}
            {...{
              ...state?.importantInfoData?.[0],
              sectionOneColummClasses: 'col-12 col-lg-6',
              sectionTwoColummClasses: 'col-12 col-lg-6'
            }}
          />}
        </main>
      </Layout>
    </div>
  );
};

export default LowRateFinderExclusivePage;
